import {
    faBan,
    faPersonDressSimple,
    faPersonSimple,
    faSteeringWheel,
} from '@fortawesome/pro-solid-svg-icons';
import {
    GetSeatsApiApiV2GetSeatsGet,
    type ServiceInfoDto,
} from '@clients/BusApi';
import type { Seat } from '@itinerary/trip/bus/types';
import { SeatStatus } from '@itinerary/trip/bus/types';
import { Gender } from '@store/form';
import { PaxType } from '@utils/types';
import type Bus from '@itinerary/trip/bus/Bus';
import PassengerForm from '@itinerary/PassengerForm';

export interface BusSummary {
    status: SeatStatus;
    numbers: Record<number, number>;
}

class BusSelectedSeat {
    departureNumber = 0;
    returnNumber = 0;
    status: SeatStatus = SeatStatus.Empty;
    isRoundTrip = false;
    get subtitle() {
        if (this.isRoundTrip) {
            const resultSubTitles: string[] = [];
            if (this.departureNumber) {
                resultSubTitles.push(`صندلی ${this.departureNumber} رفت`);
            }
            if (this.returnNumber) {
                resultSubTitles.push(`صندلی ${this.returnNumber} برگشت`);
            }
            return resultSubTitles.join(' و ');
        } else {
            if (this.departureNumber) {
                return `صندلی ${this.departureNumber}`;
            }
            return '';
        }
    }
}
const departureSeatsInfo = ref<ServiceInfoDto>({});
const returnSeatsInfo = ref<ServiceInfoDto>({});
const departureSeatsInfoLoading = ref(true);
const returnSeatsInfoLoading = ref(true);
const selectedSeats = ref<BusSelectedSeat[]>([]);

class SeatImpl implements Seat {
    constructor(
        private seat: Seat,
        private mode: 'departure' | 'return'
    ) {}

    private getIcon(status: SeatStatus) {
        if (status === SeatStatus.Wheel) {
            return faSteeringWheel;
        }
        if (status === SeatStatus.Male) {
            return faPersonSimple;
        }
        if (status === SeatStatus.Female) {
            return faPersonDressSimple;
        }
        return faBan;
    }

    get icon() {
        return this.getIcon(this.status);
    }

    get number() {
        return this.seat.number || -1;
    }

    get status() {
        return (
            selectedSeats.value.find(
                s => s[`${this.mode}Number` as const] === this.number
            )?.status ?? this.seat.status
        );
    }

    get selected() {
        return selectedSeats.value.some(
            s => s[`${this.mode}Number` as const] === this.number
        );
    }
}

export function useBusSeatHandler() {
    const formStore = useFormStore();
    const autoReserveStore = useAutoReserveStore();
    const invoiceStore = useInvoiceStore();
    const { paxForms } = classToRefs(formStore);

    function reset() {
        departureSeatsInfo.value = {};
        returnSeatsInfo.value = {};
        departureSeatsInfoLoading.value = true;
        returnSeatsInfoLoading.value = true;
        selectedSeats.value = [];
    }

    const buses = computed<Bus[]>(() =>
        formStore.itineraryPackages.flatMap(pkg => {
            if (pkg.isBusPackage() && !pkg.isSelectedToBeExclusive) {
                return pkg.segments[0].trips;
            }
            return [];
        })
    );

    const hasSeatSelection = computed(
        () => !!buses.value.length && !autoReserveStore.isAutoReserve
    );

    async function loadSeats() {
        const loadActions = buses.value.map((bus, index) => async () => {
            const { data } = await GetSeatsApiApiV2GetSeatsGet({
                busId: bus.id,
                convertFemaleToMale: false,
            });
            if (index) {
                returnSeatsInfo.value = data;
                returnSeatsInfoLoading.value = false;
            } else {
                departureSeatsInfo.value = data;
                departureSeatsInfoLoading.value = false;
            }
        });
        await Promise.all(loadActions.map(p => p()));
    }

    const isValid = computed(() => {
        if (!hasSeatSelection.value) {
            return true;
        }
        if (
            selectedSeats.value.some(
                selectedSeat =>
                    selectedSeat.isRoundTrip && !selectedSeat.returnNumber
            )
        ) {
            return false;
        }
        return selectedSeats.value.length >= minCount.value;
    });

    const passCount = computed(() =>
        Math.max(minCount.value, selectedSeats.value.length)
    );

    const h2Names = computed(() =>
        buses.value.map(bus => {
            const originName = bus.originCityName || '';
            const destinationName = bus.destinationCityName || '';
            return originName && destinationName
                ? `${originName} به ${destinationName}`
                : '';
        })
    );

    const isSeatsLoading = computed(() => [
        departureSeatsInfoLoading.value,
        returnSeatsInfoLoading.value,
    ]);

    const minCount = computed(
        () => formStore.adultCount + formStore.childCount
    );

    const canAddSeat = computed(() =>
        formStore.itineraryPackages.every(pkg => pkg.isBusPackage())
    );

    const departureSeats = computed(
        () => (departureSeatsInfo.value.seats?.flat() || []) as Seat[]
    );

    const returnSeats = computed(
        () => (returnSeatsInfo.value.seats?.flat() || []) as Seat[]
    );

    const isRoundTrip = computed(() => buses.value.length > 1);

    const seatMapping = computed<Seat[][][]>(() => [
        departureSeatsInfo.value.seats?.map(seatRow =>
            seatRow.map((seat: Seat) => new SeatImpl(seat, 'departure'))
        ) || [],
        returnSeatsInfo.value?.seats?.map(seatRow =>
            seatRow.map((seat: Seat) => new SeatImpl(seat, 'return'))
        ) || [],
    ]);

    const seatsInfo = computed(() => [
        departureSeatsInfo.value,
        returnSeatsInfo.value,
    ]);

    function updateGender(seatIndex: number, gender: Gender) {
        if (!hasSeatSelection.value || gender === Gender.None) {
            return;
        }
        const selectedSeat = selectedSeats.value[seatIndex];
        if (!selectedSeat) {
            return;
        }
        selectedSeat.status =
            gender === Gender.Male ? SeatStatus.Male : SeatStatus.Female;
    }

    function removeSeat(seatIndex: number) {
        if (!hasSeatSelection.value) {
            return;
        }
        const selectedSeat = selectedSeats.value[seatIndex];
        if (!selectedSeat) {
            return;
        }
        selectedSeats.value = selectedSeats.value.filter(
            seat => seat !== selectedSeat
        );
    }

    //#region toggleSeat: all codes below are meant to be used inside toggleSeat function and not independently.
    function toggleSeat(
        busIndex: number,
        seatNumber: number
    ): { type: 'add' } | { type: 'remove'; index: number } | undefined {
        if (seatNumber < 0) {
            return;
        }
        const status = getSeatStatus(busIndex, seatNumber);
        switch (status) {
            case SeatStatus.Empty:
                return handleEmptySeatToggle(busIndex, seatNumber);
            case SeatStatus.Male:
                handleMaleSeatToggle(busIndex, seatNumber);
                break;
            case SeatStatus.Female:
                return handleFemaleSeatToggle(busIndex, seatNumber);
        }
    }

    function getSeatStatus(busIndex: number, seatNumber: number) {
        const seat = selectedSeats.value.find(seat =>
            busIndex
                ? seat.returnNumber === seatNumber
                : seat.departureNumber === seatNumber
        );
        if (seat) {
            return seat.status;
        }
        return (busIndex ? returnSeats.value : departureSeats.value).find(
            seat => seat.number === seatNumber
        )?.status;
    }

    const canAddNewSeat = computed(
        () => selectedSeats.value.length !== minCount.value || canAddSeat.value
    );

    function handleEmptySeatToggle(busIndex: number, seatNumber: number) {
        // find the first orphan seat
        const orphanSeatIndex = findOrphanSeatIndex(busIndex);
        if (orphanSeatIndex !== -1) {
            updateOrphanSeat(busIndex, seatNumber, orphanSeatIndex);
        } else if (canAddNewSeat.value) {
            return addNewSeat(busIndex, seatNumber);
        }
    }

    function findOrphanSeatIndex(busIndex: number) {
        return selectedSeats.value.findIndex(seat =>
            busIndex ? !seat.returnNumber : !seat.departureNumber
        );
    }

    function updateOrphanSeat(
        busIndex: number,
        seatNumber: number,
        orphanSeatIndex: number
    ) {
        const orphanSeat = selectedSeats.value[orphanSeatIndex];
        orphanSeat[busIndex ? 'returnNumber' : 'departureNumber'] = seatNumber;
        orphanSeat.status = SeatStatus.Male;
        const paxForm = paxForms.value[orphanSeatIndex];
        paxForm.metadata = {
            gender: Gender.Male,
        };
        paxForm.subTitle = orphanSeat.subtitle;
    }

    function addNewSeat(busIndex: number, seatNumber: number) {
        const busSelectedSeat = new BusSelectedSeat();
        busSelectedSeat.isRoundTrip = buses.value.length > 1;
        busSelectedSeat[busIndex ? 'returnNumber' : 'departureNumber'] =
            seatNumber;
        busSelectedSeat.status = SeatStatus.Male;
        selectedSeats.value.push(busSelectedSeat);
        if (selectedSeats.value.length > paxForms.value.length) {
            return addNewPaxForm(busSelectedSeat, busIndex);
        } else {
            const paxForm = paxForms.value[selectedSeats.value.length - 1];
            paxForm.metadata = {
                gender: Gender.Male,
            };
            paxForm.subTitle = busSelectedSeat.subtitle;
        }
    }

    function addNewPaxForm(busSelectedSeat: BusSelectedSeat, busIndex: number) {
        const newForm = new PassengerForm({
            ...paxForms.value[0],
            subTitle: busSelectedSeat.subtitle,
        });
        newForm.metadata = {
            gender: Gender.Male,
        };
        paxForms.value.push(newForm);
        formStore.adultCount++;
        updatePriceEntries(busIndex);
        return { type: 'add' } as const;
    }

    function handleMaleSeatToggle(busIndex: number, seatNumber: number) {
        const seatIndex = selectedSeats.value.findIndex(seat =>
            busIndex
                ? seat.returnNumber === seatNumber
                : seat.departureNumber === seatNumber
        );
        selectedSeats.value[seatIndex].status = SeatStatus.Female;
        paxForms.value[seatIndex].metadata!.gender = Gender.Female;
    }

    function handleFemaleSeatToggle(busIndex: number, seatNumber: number) {
        const selectedSeatIndex = selectedSeats.value.findIndex(seat =>
            busIndex
                ? seat.returnNumber === seatNumber
                : seat.departureNumber === seatNumber
        );
        const selectedSeat = selectedSeats.value[selectedSeatIndex];
        if (!selectedSeat) {
            return;
        }
        if (!selectedSeat[busIndex ? 'departureNumber' : 'returnNumber']) {
            selectedSeats.value = selectedSeats.value.filter(
                seat => seat !== selectedSeat
            );
            if (paxForms.value.length > 1) {
                paxForms.value.splice(selectedSeatIndex, 1);
                formStore.adultCount--;
                updatePriceEntries(busIndex);
                return { type: 'remove', index: selectedSeatIndex } as const;
            }
        } else {
            selectedSeat[busIndex ? 'returnNumber' : 'departureNumber'] = 0;
        }
    }

    function updatePriceEntries(busIndex: number) {
        if (isRoundTrip.value) {
            invoiceStore.changeEntryPriceCountByPaxType({
                entryIndex: 0,
                paxType: PaxType.Seat,
                count: selectedSeats.value.length,
            });
            invoiceStore.changeEntryPriceCountByPaxType({
                entryIndex: 1,
                paxType: PaxType.Seat,
                count: selectedSeats.value.length,
            });
        } else {
            invoiceStore.changeEntryPriceCountByPaxType({
                entryIndex: busIndex,
                paxType: PaxType.Seat,
                count: selectedSeats.value.length,
            });
        }
    }
    //#endregion

    const summary = computed<BusSummary[]>(() =>
        selectedSeats.value.map(seat => ({
            status: seat.status as unknown as SeatStatus,
            numbers: {
                [buses.value[0]?.id]: seat.departureNumber,
                [buses.value[1]?.id]: seat.returnNumber,
            },
        }))
    );

    return {
        isValid,
        passCount,
        h2Names,
        isSeatsLoading,
        seatMapping,
        seatsInfo,
        hasSeatSelection,
        buses,
        selectedSeats,
        summary,
        loadSeats,
        updateGender,
        removeSeat,
        toggleSeat,
        reset,
    };
}
